import * as React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { BudgetProgress, type BudgetProgressSegmentKey } from '@/components/ui/Progress';
import {
  BudgetProgressSegmentDialog,
  type ProgressSegmentModalKind,
} from '@/components/budget/BudgetProgressSegmentDialog';
import { cn, formatCurrency } from '@/lib/utils';
import type { Budget } from '@/types';

interface BudgetOverviewSectionProps {
  budget: Budget;
  parentBudget: Budget | null;
  fxRateLoading: boolean;
  convertedAmountToParent: number | null;
  effectivePercentSpent: number;
  effectiveRemaining: number;
  unallocated?: number;
  totalRolloverAmount: number;
  rolloverChildren: Budget[];
  ownRolloverAmount: number;
  budgetAmountWithChildRollover: number;
  onOpenBudget: (budgetId: string) => void;
  /** Scroll to this budget's transaction list (Direct segment). */
  onScrollToTransactions?: () => void;
  /** Accept dragged transactions onto this (parent) budget */
  onTransactionDrop?: (transactionId: string) => void;
}

export function BudgetOverviewSection({
  budget,
  parentBudget,
  fxRateLoading,
  convertedAmountToParent,
  effectivePercentSpent,
  effectiveRemaining,
  unallocated,
  totalRolloverAmount,
  rolloverChildren,
  ownRolloverAmount,
  budgetAmountWithChildRollover,
  onOpenBudget,
  onScrollToTransactions,
  onTransactionDrop,
}: BudgetOverviewSectionProps) {
  const [showRolloverMenu, setShowRolloverMenu] = React.useState(false);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [segmentModal, setSegmentModal] = React.useState<ProgressSegmentModalKind | null>(null);
  const rolloverMenuRef = React.useRef<HTMLDivElement>(null);
  const dragEnterCount = React.useRef(0);

  const spent = budget.spent ?? 0;
  const directSpent = budget.directSpent ?? spent;
  const allocated = budget.allocated ?? 0;
  const childSpent = Math.max(0, spent - directSpent);
  // Remaining of child allocations not yet paid
  const remainingPayments = Math.max(0, allocated - childSpent);
  const displayUnallocated =
    unallocated ?? Math.max(0, budget.amount - allocated - directSpent);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rolloverMenuRef.current && !rolloverMenuRef.current.contains(event.target as Node)) {
        setShowRolloverMenu(false);
      }
    };

    if (showRolloverMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showRolloverMenu]);

  const handleSegmentClick = (key: BudgetProgressSegmentKey) => {
    if (key === 'direct') {
      onScrollToTransactions?.();
      return;
    }
    if (key === 'children' || key === 'allocated') {
      setSegmentModal(key);
    }
  };

  return (
    <>
      <Card
        data-testid={`budget-parent-drop-${budget.id}`}
        className={cn(
          'relative transition-all',
          isDragOver && 'ring-2 ring-primary ring-offset-1 shadow-lg'
        )}
        onDragEnter={(e) => {
          if (!onTransactionDrop) return;
          if (!e.dataTransfer.types.includes('application/x-transaction-id')) return;
          e.preventDefault();
          dragEnterCount.current += 1;
          setIsDragOver(true);
        }}
        onDragLeave={() => {
          if (!onTransactionDrop) return;
          dragEnterCount.current -= 1;
          if (dragEnterCount.current === 0) setIsDragOver(false);
        }}
        onDragOver={(e) => {
          if (!onTransactionDrop) return;
          if (!e.dataTransfer.types.includes('application/x-transaction-id')) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
        }}
        onDrop={(e) => {
          if (!onTransactionDrop) return;
          e.preventDefault();
          e.stopPropagation();
          dragEnterCount.current = 0;
          setIsDragOver(false);
          const transactionId = e.dataTransfer.getData('application/x-transaction-id');
          if (transactionId) onTransactionDrop(transactionId);
        }}
      >
        {isDragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-primary/10 border-2 border-dashed border-primary pointer-events-none">
            <span className="text-sm font-semibold text-primary">Drop here → {budget.name}</span>
          </div>
        )}
        <CardContent className="p-4">
          <BudgetProgress
            spent={budget.spent ?? 0}
            directSpent={budget.directSpent}
            allocated={budget.allocated}
            budget={budget.amount}
            currency={budget.currency}
            percentSpent={effectivePercentSpent}
            remaining={effectiveRemaining}
            unallocated={unallocated}
            rolloverAmount={totalRolloverAmount || undefined}
            onRolloverClick={
              rolloverChildren.length > 0
                ? () => setShowRolloverMenu((prev) => !prev)
                : undefined
            }
            onSegmentClick={handleSegmentClick}
          />
        </CardContent>
      </Card>

      <BudgetProgressSegmentDialog
        open={segmentModal !== null}
        kind={segmentModal}
        budget={budget}
        onOpenChange={(open) => {
          if (!open) setSegmentModal(null);
        }}
        onOpenBudget={onOpenBudget}
      />

      <div
        className={
          ownRolloverAmount > 0
            ? 'grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5 md:gap-3'
            : 'grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3'
        }
      >
        <Card>
          <CardContent className="p-3 text-center relative" ref={rolloverMenuRef}>
            <p className="text-xs text-muted-foreground">Budget</p>
            <p className="font-semibold text-foreground text-sm md:text-base truncate">
              {formatCurrency(budgetAmountWithChildRollover, budget.currency)}
            </p>
            {showRolloverMenu && rolloverChildren.length > 0 && (
              <div className="absolute left-1/2 z-20 mt-2 w-64 -translate-x-1/2 rounded-md border border-input bg-background p-2 text-left shadow-md">
                <p className="px-2 py-1 text-xs font-medium text-muted-foreground">Rollover children</p>
                <div className="max-h-52 overflow-y-auto">
                  {rolloverChildren.map((child) => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => {
                        setShowRolloverMenu(false);
                        onOpenBudget(child.id);
                      }}
                      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <span className="truncate text-foreground">{child.name}</span>
                      <span className="ml-2 text-success">+{formatCurrency(child.rolloverAmount ?? 0, child.currency)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {parentBudget && parentBudget.currency !== budget.currency && (
              <p className="text-xs text-muted-foreground mt-1">
                ≈ {fxRateLoading || convertedAmountToParent === null
                  ? '—'
                  : formatCurrency(convertedAmountToParent, parentBudget.currency)}{' '}
                ({parentBudget.currency})
              </p>
            )}
          </CardContent>
        </Card>

        {ownRolloverAmount > 0 && (
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-xs text-muted-foreground">Rollover</p>
              <p className="font-semibold text-success">
                +{formatCurrency(ownRolloverAmount, budget.currency)}
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <span className="md:hidden" title="Direct Spend">Direct</span>
              <span className="hidden md:inline">Direct Spend</span>
            </p>
            <p className="font-semibold text-foreground text-sm md:text-base truncate">
              {formatCurrency(directSpent, budget.currency)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <span className="md:hidden" title="Remaining payments">Remaining</span>
              <span className="hidden md:inline">Remaining payments</span>
            </p>
            <p className="font-semibold text-foreground text-sm md:text-base truncate">
              {formatCurrency(remainingPayments, budget.currency)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">
              <span className="md:hidden" title="Unallocated">Unalloc.</span>
              <span className="hidden md:inline">Unallocated</span>
            </p>
            <p className="font-semibold text-foreground text-sm md:text-base truncate">
              {formatCurrency(displayUnallocated, budget.currency)}
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}